import { deleteUploadedFile, type UploadedFileResource } from '@repo/ui/api/file-upload';

type UploadBatchState = 'pending' | 'uncertain' | 'persisted' | 'cleaning' | 'cleaned';
export type UploadCleanupResult = 'cleaned' | 'retained';

interface UploadBatchOptions {
  onCleanup?: () => void;
}

export class UploadCleanupError extends AggregateError {
  readonly failures: readonly unknown[];

  constructor(message: string, failures: readonly unknown[]) {
    super(failures, message);
    this.name = 'UploadCleanupError';
    this.failures = failures;
  }
}

export function containsUploadCleanupError(error: unknown): boolean {
  if (error instanceof UploadCleanupError) return true;
  if (error instanceof AggregateError) {
    const nestedErrors: unknown[] = error.errors;
    return nestedErrors.some(containsUploadCleanupError);
  }
  return error instanceof Error && error.cause !== undefined && containsUploadCleanupError(error.cause);
}

export interface UploadBatch<T> {
  value: T;
  uploadedFiles: readonly UploadedFileResource[];
  cleanup: () => Promise<UploadCleanupResult>;
  markPersisted: () => void;
  markUncertain: () => void;
  confirmNotPersisted: () => void;
  isUncertain: () => boolean;
  isReusable: () => boolean;
}

export class UploadMutationPersistenceError extends Error {
  constructor() {
    super('The server response did not confirm whether the uploaded files were saved. The files were retained.');
    this.name = 'UploadMutationPersistenceError';
  }
}

export class UploadSubmissionNotPersistedError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = 'UploadSubmissionNotPersistedError';
  }
}

function uniqueUploadedFiles(files: readonly UploadedFileResource[]): UploadedFileResource[] {
  return [...new Map(files.map(file => [file.id, file])).values()];
}

async function cleanupUploadedFiles(files: Map<string, UploadedFileResource>): Promise<void> {
  const cleanupResults = await Promise.allSettled(
    [...files].map(async ([id, file]) => {
      await deleteUploadedFile(file);
      return id;
    }),
  );
  const cleanupFailures: unknown[] = [];

  for (const result of cleanupResults) {
    if (result.status === 'fulfilled') {
      files.delete(result.value);
    } else {
      cleanupFailures.push(result.reason);
    }
  }

  if (cleanupFailures.length > 0) {
    throw new UploadCleanupError('Failed to clean up one or more uploaded files', cleanupFailures);
  }
}

export function createUploadBatch<T>(
  value: T,
  uploadedFiles: readonly UploadedFileResource[],
  options: UploadBatchOptions = {},
): UploadBatch<T> {
  const ownedFiles = uniqueUploadedFiles(uploadedFiles);
  const filesPendingCleanup = new Map(ownedFiles.map(file => [file.id, file]));
  let state: UploadBatchState = 'pending';
  let cleanupAttempted = false;
  let cleanupPromise: Promise<UploadCleanupResult> | null = null;

  return {
    value,
    uploadedFiles: ownedFiles,
    cleanup: () => {
      if (state === 'persisted' || state === 'uncertain') return Promise.resolve('retained');
      if (state === 'cleaned') return Promise.resolve('cleaned');
      if (cleanupPromise) return cleanupPromise;

      state = 'cleaning';
      cleanupAttempted = true;
      cleanupPromise = cleanupUploadedFiles(filesPendingCleanup)
        .then(() => {
          state = 'cleaned';
          options.onCleanup?.();
          return 'cleaned' as const;
        })
        .catch((error: unknown) => {
          state = 'pending';
          throw error;
        })
        .finally(() => {
          cleanupPromise = null;
        });
      return cleanupPromise;
    },
    markPersisted: () => {
      if (state === 'pending' || state === 'uncertain') state = 'persisted';
    },
    markUncertain: () => {
      if (state === 'pending') state = 'uncertain';
    },
    confirmNotPersisted: () => {
      if (state === 'uncertain') state = 'pending';
    },
    isUncertain: () => state === 'uncertain',
    isReusable: () => !cleanupAttempted && (state === 'pending' || state === 'uncertain' || state === 'persisted'),
  };
}

export function combineUploadBatches<T>(value: T, batches: readonly UploadBatch<unknown>[]): UploadBatch<T> {
  if (batches.length === 0) return createUploadBatch(value, []);
  return {
    value,
    uploadedFiles: uniqueUploadedFiles(batches.flatMap(batch => batch.uploadedFiles)),
    cleanup: async () => {
      const cleanupResults = await Promise.allSettled(batches.map(batch => batch.cleanup()));
      const cleanupFailures = cleanupResults.flatMap(result => (result.status === 'rejected' ? [result.reason] : []));
      if (cleanupFailures.length > 0) {
        throw new UploadCleanupError('Failed to clean up one or more upload groups', cleanupFailures);
      }
      return cleanupResults.some(result => result.status === 'fulfilled' && result.value === 'retained')
        ? 'retained'
        : 'cleaned';
    },
    markPersisted: () => {
      for (const batch of batches) batch.markPersisted();
    },
    markUncertain: () => {
      for (const batch of batches) batch.markUncertain();
    },
    confirmNotPersisted: () => {
      for (const batch of batches) batch.confirmNotPersisted();
    },
    isUncertain: () => batches.some(batch => batch.isUncertain()),
    isReusable: () => batches.every(batch => batch.isReusable()),
  };
}

export async function collectUploadBatchCleanupFailures(batches: readonly UploadBatch<unknown>[]): Promise<unknown[]> {
  const cleanupResults = await Promise.allSettled(batches.map(batch => batch.cleanup()));
  return cleanupResults.flatMap(result => (result.status === 'rejected' ? [result.reason] : []));
}

export async function settleUploadBatches<TFirst, TSecond>(
  firstUpload: Promise<UploadBatch<TFirst>>,
  secondUpload: Promise<UploadBatch<TSecond>>,
): Promise<UploadBatch<{ first: TFirst; second: TSecond }>> {
  const [firstResult, secondResult] = await Promise.allSettled([firstUpload, secondUpload]);

  if (firstResult.status === 'fulfilled' && secondResult.status === 'fulfilled') {
    return combineUploadBatches({ first: firstResult.value.value, second: secondResult.value.value }, [
      firstResult.value,
      secondResult.value,
    ]);
  }

  const completedBatches = [firstResult, secondResult].flatMap(result =>
    result.status === 'fulfilled' ? [result.value] : [],
  );
  const uploadFailures = [firstResult, secondResult].flatMap(result =>
    result.status === 'rejected' ? [result.reason] : [],
  );
  const cleanupFailures = await collectUploadBatchCleanupFailures(completedBatches);

  if (cleanupFailures.length > 0 || uploadFailures.some(containsUploadCleanupError)) {
    throw new UploadCleanupError('One or more upload groups failed and completed uploads could not all be cleaned up', [
      ...uploadFailures,
      ...cleanupFailures,
    ]);
  }

  if (uploadFailures.length > 1) {
    throw new AggregateError(uploadFailures, 'Multiple upload groups failed');
  }

  throw new Error('One or more upload groups failed', { cause: uploadFailures[0] });
}

export async function settleUploadBatchesOrNull<TFirst, TSecond>(
  firstUpload: Promise<UploadBatch<TFirst>>,
  secondUpload: Promise<UploadBatch<TSecond>>,
): Promise<UploadBatch<{ first: TFirst; second: TSecond }> | null> {
  return await settleUploadBatches(firstUpload, secondUpload).then(
    uploadBatch => uploadBatch,
    (error: unknown) => {
      if (containsUploadCleanupError(error)) throw error;
      return null;
    },
  );
}

async function throwSubmissionFailure(batch: UploadBatch<unknown>, error: unknown): Promise<never> {
  if (error instanceof UploadSubmissionNotPersistedError && !batch.isUncertain()) {
    try {
      await batch.cleanup();
    } catch (cleanupError) {
      throw new UploadCleanupError('The mutation failed and uploaded files could not be cleaned up', [
        error,
        cleanupError,
      ]);
    }
  } else {
    batch.markUncertain();
  }
  throw error;
}

function getResolvedSubmissionError(result: unknown): unknown {
  if (typeof result !== 'object' || result === null || !('error' in result)) return undefined;
  return result.error;
}

export async function submitUploadBatch<T>(
  batch: UploadBatch<unknown>,
  submit: () => Promise<T>,
  confirmsPersistence: (result: T) => boolean,
  confirmsNonPersistence?: (result: T) => boolean,
): Promise<T> {
  const submission = await Promise.resolve()
    .then(submit)
    .then(
      result => ({ succeeded: true, result }) as const,
      (error: unknown) => ({ succeeded: false, error }) as const,
    );
  if (!submission.succeeded) return await throwSubmissionFailure(batch, submission.error);
  const { result } = submission;

  if (confirmsPersistence(result)) {
    batch.markPersisted();
    return result;
  }

  if (confirmsNonPersistence?.(result)) {
    return await throwSubmissionFailure(
      batch,
      new UploadSubmissionNotPersistedError('The server confirmed that the mutation was rolled back'),
    );
  }

  const resolvedSubmissionError = getResolvedSubmissionError(result);
  if (resolvedSubmissionError !== undefined && resolvedSubmissionError !== null) {
    return await throwSubmissionFailure(batch, resolvedSubmissionError);
  }

  batch.markUncertain();
  throw new UploadMutationPersistenceError();
}

export async function submitUploadBatchWithPersistenceErrorHandler<T>(
  batch: UploadBatch<unknown>,
  submit: () => Promise<T>,
  confirmsPersistence: (result: T) => boolean,
  onPersistenceError: (error: UploadMutationPersistenceError) => void,
  confirmsNonPersistence?: (result: T) => boolean,
): Promise<void> {
  await submitUploadBatch(batch, submit, confirmsPersistence, confirmsNonPersistence).catch((error: unknown) => {
    if (!(error instanceof UploadMutationPersistenceError)) throw error;
    onPersistenceError(error);
  });
}

export async function throwUploadFailureAfterCleanup(
  completedUploads: readonly UploadedFileResource[],
  uploadFailure: unknown,
): Promise<never> {
  try {
    await cleanupUploadedFiles(new Map(uniqueUploadedFiles(completedUploads).map(file => [file.id, file])));
  } catch (cleanupError) {
    throw new UploadCleanupError('One or more files failed to upload and completed uploads could not be cleaned up', [
      uploadFailure,
      ...(cleanupError instanceof UploadCleanupError ? cleanupError.failures : [cleanupError]),
    ]);
  }

  throw new Error('One or more files failed to upload', { cause: uploadFailure });
}
